import { normalizeDecimal } from "./decimal";

// ---------------------------------------------------------------------------
// Core formatter
// ---------------------------------------------------------------------------

export function fmtDecimal(val: string, decimals: number): string {
  if (!val) return val;
  const stripped = val.replace(/^\+/, "");
  const num = parseFloat(stripped);
  if (isNaN(num)) return val;
  if (num === 0) return (0).toFixed(decimals);
  const sign = val.startsWith("-") ? "-" : val.startsWith("+") ? "+" : "";
  return `${sign}${Math.abs(num).toFixed(decimals)}`;
}

// ---------------------------------------------------------------------------
// OCR extractors
// ---------------------------------------------------------------------------

export function ocrVal(val?: string): string {
  return val?.trim() || "";
}

export function ocrNum(val?: string): string {
  if (!val) return "";
  const match = val.match(/[+-]?\d+(\.\d+)?/);
  return match ? match[0] : "";
}

export function ocrFixDecimal(val: string, threshold: number): string {
  if (!val) return val;
  if (val.includes(".")) return val;
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  if (Math.abs(num) >= threshold) return String(num / 100);
  return val;
}

export function ocrMeasurement(val?: string, decimals = 1): string {
  const extracted = ocrNum(val);
  if (!extracted) return "";
  return fmtDecimal(extracted, decimals);
}

export function ocrSigned(val?: string): string {
  const extracted = ocrNum(val);
  if (!extracted) return "";
  const num = parseFloat(extracted);
  if (isNaN(num) || num === 0) return extracted;
  return num > 0 ? `+${extracted}` : extracted;
}

// ---------------------------------------------------------------------------
// onBlur format helpers
// ---------------------------------------------------------------------------

export function formatSph(val: string): string {
  if (!val) return val;
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  if (num === 0) return "0.00";
  const abs = Math.abs(num).toFixed(2);
  return num > 0 ? `+${abs}` : `-${abs}`;
}

export function formatCyl(val: string): string {
  if (!val) return val;
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  if (num === 0) return "0.00";
  return `-${Math.abs(num).toFixed(2)}`;
}

export function formatAdd(val: string): string {
  if (!val) return val;
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  if (num === 0) return "0.00";
  return `+${Math.abs(num).toFixed(2)}`;
}

export function formatAxis(val: string): string {
  if (!val) return val;
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  return String(Math.round(Math.abs(num)));
}

export function toggleSign(val: string): string {
  if (!val) return val;
  if (val.startsWith("+")) return `-${val.slice(1)}`;
  if (val.startsWith("-")) return `+${val.slice(1)}`;
  return `-${val}`;
}

// ---------------------------------------------------------------------------
// Payload formatters
// ---------------------------------------------------------------------------

export function payloadStr(val?: unknown): string | undefined {
  const text = normalizeDecimal(val);
  if (!text || text === "-") return undefined;
  return text;
}

export function payloadNum(val?: unknown): string {
  const text = normalizeDecimal(val);
  if (!text || text === "-") return "0";
  const num = parseFloat(text);
  if (isNaN(num)) return text;
  if (num === 0) return "0";
  return text;
}

export function payloadSigned(val?: unknown): string {
  const text = normalizeDecimal(val);
  if (!text || text === "-") return "0";
  const num = parseFloat(text);
  if (isNaN(num)) return text;
  if (num === 0) return "0";
  if (num > 0 && !text.startsWith("+")) return `+${text}`;
  return text;
}

// ---------------------------------------------------------------------------
// Composed helper — clampField + field-specific formatting
// ---------------------------------------------------------------------------

import { clampField } from "./orderDisplay";

export function clampAndFormat(
  field: "sphere" | "cylinder" | "axis" | "add",
  raw: string,
): string {
  const clamped = clampField(field, raw);
  if (!clamped) return clamped;
  switch (field) {
    case "sphere":
      return formatSph(clamped);
    case "cylinder":
      return formatCyl(clamped);
    case "add":
      return formatAdd(clamped);
    case "axis":
      return formatAxis(clamped);
  }
}
